import { immerable } from 'immer'
import { Edge } from '../../entities'
import type { LayoutDirection, TreeEdgeData } from '../../types'
import { getTextTransform } from '../../utils'
import type { TreeGraph } from '../tree-graph'
import type { TreeNode } from '.'

type Point = [number, number]

export class TreeEdge extends Edge {
  [immerable] = true

  layoutDirection?: LayoutDirection

  nodes?: TreeNode[]

  constructor(graph: TreeGraph, edgeData: TreeEdgeData, nodes?: TreeNode[]) {
    super(graph, edgeData)
    this.layoutDirection = edgeData.layoutDirection
    this.nodes = nodes
  }

  get sourceNode(): TreeNode {
    return (this.nodes || (this.graph.nodes as TreeNode[])).find(
      (node) => node.id === this.source
    )!
  }

  get targetNode(): TreeNode {
    return (this.nodes || (this.graph.nodes as TreeNode[])).find(
      (node) => node.id === this.target
    )!
  }

  get isHorizontal() {
    return ['LR', 'RL', 'H'].includes(this.layoutDirection || '')
  }

  get isReverseLayoutDirection() {
    return ['RL', 'BT'].includes(this.layoutDirection || '')
  }

  get isTargetNodeLeftSide() {
    return this.targetNode?.side === 'left' || this.isReverseLayoutDirection
  }

  /**
   * 获取边的路径
   *
   * @returns 边路径
   */
  getPath = () => {
    const { sourceNode, targetNode, isTargetNodeLeftSide } = this
    const startPoint: Point = [
      sourceNode.x + sourceNode.width,
      sourceNode.y + sourceNode.height / 2,
    ]
    const endPoint: Point = [targetNode.x, targetNode.y + targetNode.height / 2]

    if (!this.isHorizontal) {
      startPoint[0] = sourceNode.x + sourceNode.width / 2
      startPoint[1] = sourceNode.y + sourceNode.height
      endPoint[0] = targetNode.x + targetNode.width / 2
      endPoint[1] = targetNode.y
    }

    if (isTargetNodeLeftSide) {
      if (this.isHorizontal) {
        startPoint[0] = sourceNode.x
        endPoint[0] = targetNode.x + targetNode.width
      } else {
        startPoint[1] = sourceNode.y
        endPoint[1] = targetNode.y + targetNode.height
      }
    }

    const controlPoints = this.getControlPoints(startPoint, endPoint)

    const d = `M${startPoint.join(' ')}${controlPoints
      .map(([x, y]) => `L${x} ${y}`)
      .join('')}L${endPoint.join(' ')}`
    // const textPoint = getTextPoint(d)
    // const textRotate = getRotate(sourceNode, targetNode)
    // const textTransform = getTextTransform(textPoint, textRotate)
    const textPoint = this.isHorizontal
      ? {
          x: controlPoints[1][0] + (endPoint[0] - controlPoints[1][0]) / 2,
          y: endPoint[1],
        }
      : {
          x: controlPoints[1][0],
          y: controlPoints[1][1] + (endPoint[1] - controlPoints[1][1]) / 2 + 8,
        }
    const textTransform = getTextTransform(textPoint, 0)

    return {
      d,
      textPoint,
      textTransform,
    }
  }

  getControlPoints(startPoint: Point, endPoint: Point): Point[] {
    const { sourceNode, targetNode, isHorizontal, isTargetNodeLeftSide } = this
    const factor = isHorizontal ? 0.25 : 0.4
    const firstPoint: Point = [
      startPoint[0],
      startPoint[1] +
        (targetNode.y +
          (isTargetNodeLeftSide ? targetNode.height : 0) -
          sourceNode.y -
          (isTargetNodeLeftSide ? 0 : sourceNode.height)) *
          factor,
    ]

    if (isHorizontal) {
      firstPoint[0] =
        startPoint[0] +
        (targetNode.x +
          (isTargetNodeLeftSide ? targetNode.width : 0) -
          sourceNode.x -
          (isTargetNodeLeftSide ? 0 : sourceNode.width)) *
          factor
      firstPoint[1] = startPoint[1]

      return [firstPoint, [firstPoint[0], endPoint[1]]]
    }

    return [firstPoint, [endPoint[0], firstPoint[1]]]
  }
}
